<template>
  <div>
    <div :class="$style.formGroup">
      <label for="shopName" :class="$style.label">Shop name *</label>
      <InputText
        id="shopName"
        v-model="shopName"
        :class="{ [$style.pInvalid]: shopName && (!isValid || isDuplicate) }"
        placeholder="Shop identifier"
        autofocus
        @input="validate"
        @keydown.enter="save"
      />
      <small :class="[$style.info, shopName && !isValid ? $style.pError : $style.pValid]">
        Use only alphanumeric characters
      </small>
      <small v-if="isDuplicate" :class="[$style.info, $style.pError]">Shop name already exists</small>
    </div>
    <small :class="[$style.info, $style.pValid]">* = required</small>
    <div :class="$style.actions">
      <Button label="Cancel" class="p-button-primary" outlined @click="cancel" />
      <Button
        :label="props.shop ? 'Update' : 'Save'"
        class="p-button-primary"
        :disabled="!isValid || isDuplicate"
        @click="save"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import isAlphaNumeric from "@/utils/isAlphaNumeric";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import useShopsManager from "../hooks/useShopsManager";
import { CreateShopPayload, Shop } from "../shop.types";

const route = useRoute();
const appId = route.params.gameId as string;
const versionId = route.params.versionId as string;
const props = defineProps<{
  shop: Shop | null;
}>();

const shopName = ref(props.shop?.name || "");
const isValid = ref(false);
const isDuplicate = ref(false);
const emit = defineEmits(["save-shop", "close"]);
const { isDuplicateName } = useShopsManager(appId, versionId);

const validate = () => {
  const initialValue = props.shop?.name;
  isValid.value = isAlphaNumeric({ str: shopName.value, allowWhitespace: true });
  isDuplicate.value = initialValue !== shopName.value && isDuplicateName(shopName.value);
};

const cancel = () => {
  shopName.value = "";
  isValid.value = false;
  isDuplicate.value = false;
  emit("close");
};

function resetForm() {
  shopName.value = "";
  isValid.value = false;
  isDuplicate.value = false;
}
const save = () => {
  validate();
  if (isValid.value && !isDuplicate.value) {
    emit("save-shop", {
      name: shopName.value,
      isDefault: props.shop?.isDefault || false,
      appId,
    } as CreateShopPayload);
    resetForm();
  }
};
watch(
  () => props.shop,
  () => {
    validate();
  },
  {
    immediate: true,
  }
);
</script>

<style module>
.formGroup {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.formGroupRow {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
}

.label {
  margin-bottom: 0.5rem;
}

.pInvalid {
  border-color: var(--vd-color-error);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pError {
  color: var(--vd-color-error);
}

.pValid {
  color: var(--vd-color-text-light-2);
}

.info {
  margin-top: 0.5rem;
}

.centerAligned {
  align-items: center;
  display: flex;
}
</style>
